import { ReadonlyArray } from "fp";

export type Search = { readonly search: string };
export namespace Search {
  export type From<A> = (a: A) => Search;

  export const string = (string: string): Search => ({ search: string });

  export const empty = () => ({ search: "" });
  export const join = (...searches: readonly Search[]): Search =>
    pipe(
      searches,
      ReadonlyArray.reduce(empty(), (previous, { search }) => ({
        search: `${previous.search} ${search}`,
      }))
    );

  export const matches = (match: Match) => ({ search }: Search): boolean =>
    Match.when(match, {
      anything: () => true,
      string: (string) =>
        search.toLocaleLowerCase().includes(string.toLocaleLowerCase().trim()),
    });

  export type Match = {
    readonly match: { readonly anything: true } | { readonly string: string };
  };

  export namespace Match {
    export const anything = (): Match => ({ match: { anything: true } });
    export const string = (string: string): Match =>
      string === "" ? anything() : { match: { string } };

    export type When<A> = {
      readonly anything: () => A;
      readonly string: (string: string) => A;
    };

    export function when<A>(match: Match, when: When<A>): A;
    export function when<A>(when: When<A>): (match: Match) => A;
    export function when<A>(
      whenOrMatch: When<A> | Match,
      whenOrUndefined?: When<A>
    ) {
      if (whenOrUndefined) return when(whenOrUndefined)(whenOrMatch as Match);

      const { anything, string } = whenOrMatch as When<A>;
      return ({ match }: Match): A =>
        "anything" in match ? anything() : string(match.string);
    }

    export const toString = when({
      anything: () => "",
      string: (string) => string,
    });
  }
}
